import {
  useDisclosure,
  useToast,
  IconButton,
  Img,
  MenuButton,
  MenuList,
  MenuItem,
  Menu,
  Portal,
  Box,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { clientEndpoints } from '@texas/clientEndpoints';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '@texas/utils/helpers/httpHelpers';
import { productApprovalsApi } from '@texas/api/endpoints/productApprovalsApi';
import { maxSize, sizeValidator } from '../dropzone/shared';
import { ExpandImage } from '../image/ExpandImage';
import { Icons, defaultIconSize } from '../Icons';
import { WarningComponent } from '../WarningComponent';
import { CapturePhoto } from '../capture/CapturePhoto';
import { FileBrowserModal } from '../files/FileBrowserModal';
import {
  acceptedImageFormats,
  imageExtensions,
} from '@texas/utils/helpers/filesHelper';
import { useDropzone } from 'react-dropzone';
import { fadeInRightAnimation } from '@texas/resources/animations/animations';
import { VerifyDialogWithRequest } from '../dialog/VerifyDialogWithRequest';
import { usePortalRefValue } from '@texas/hooks/usePortalRef';
import { productApprovalEvents } from './events';
import { CaptureSessionType } from '@texas/api/endpoints/capture/captureApi';

interface Props {
  fileIdentifier: string | null;
  fileId: number | null;
  paId: number;
  paNumber: string;
  itemCode: string;
  articleFolderId: number;
  matrixNodeXValue: string | null;
  matrixNodeYValue: string | null;
  maxW?: string;
  maxH?: string;
}

export function PaImage(props: Props) {
  const { isOpen, onClose, onOpen: onCaptureOpen } = useDisclosure();
  const {
    isOpen: isFileBrowserOpen,
    onClose: onFileBrowserClose,
    onOpen: onFileBrowserOpen,
  } = useDisclosure();
  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();
  const { ref } = usePortalRefValue();

  const { request: updateRequest, loading } = useApiRequest(
    productApprovalsApi.updatePhoto,
  );
  const [fileIdentifier, setFileIdentifier] = useState<string | null>(
    props.fileIdentifier,
  );
  const toast = useToast();
  const { t } = useTranslation();

  const updateFile = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [props.paId, file.id],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          setFileIdentifier(file.identifier);
          productApprovalEvents.productApprovalChanged.dispatch({
            id: props.paId,
          });
        },
        (_) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [props.paId, t, toast, updateRequest],
  );
  const { isUploading, uploadFiles } = useFileUploads(updateFile, maxSize);
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: (acceptedFiles) => {
      uploadFiles(acceptedFiles, props.articleFolderId);
    },
    useFsAccessApi: false,
    accept: acceptedImageFormats,
    noClick: true,
    multiple: false,
    validator: (file) => sizeValidator(file, t),
  });

  return (
    <Box {...getRootProps()}>
      {fileIdentifier ? (
        <ExpandImage
          variant="scale"
          identifier={fileIdentifier}
          hoverMenu={
            <HoverMenu
              fileIdentifier={fileIdentifier}
              onFileBrowserOpen={onFileBrowserOpen}
              onCaptureOpen={onCaptureOpen}
              open={open}
              onRemoveFileOpen={onRemoveFileOpen}
            />
          }
        >
          <Img
            maxW={props.maxW ? props.maxW : undefined}
            maxH={props.maxH ? props.maxH : undefined}
            objectFit="cover"
            src={clientEndpoints.previewImage(fileIdentifier, 256)}
          />
        </ExpandImage>
      ) : (
        <WarningComponent warning={t('sample.imageMissing')}>
          <Menu>
            <MenuButton
              as={IconButton}
              size="sm"
              icon={<Icons.Camera boxSize={defaultIconSize} />}
              aria-label="show more"
              onClick={(e) => {
                e.stopPropagation();
              }}
              isLoading={isUploading || loading}
            />
            <Portal containerRef={ref}>
              <MenuList>
                <MenuItem
                  icon={<Icons.Camera boxSize={defaultIconSize} />}
                  onClick={onCaptureOpen}
                >
                  {t('sample.captureImageWithPhone')}
                </MenuItem>
                <MenuItem
                  icon={<Icons.OpenInNew boxSize={defaultIconSize} />}
                  onClick={onFileBrowserOpen}
                >
                  {t('fileBrowser.chooseFromLibrary')}
                </MenuItem>
                <MenuItem
                  icon={<Icons.Upload boxSize={defaultIconSize} />}
                  onClick={open}
                >
                  {t('fileBrowser.uploadFile')}
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </WarningComponent>
      )}
      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removePhoto')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={productApprovalsApi.deletePhoto}
        args={[props.paId]}
        isOpen={isRemoveFileOpen}
        onClose={onRemoveFileClose}
        onPerformed={() => {
          setFileIdentifier(null);
          productApprovalEvents.productApprovalChanged.dispatch({
            id: props.paId,
          });
        }}
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
      <FileBrowserModal
        rootFolderId={props.articleFolderId}
        mode="Select"
        isOpen={isFileBrowserOpen}
        onClose={onFileBrowserClose}
        extensionFilter={imageExtensions}
        onSelect={(file: FileLink) => {
          updateFile(file);
          onFileBrowserClose();
        }}
      />
      <CapturePhoto
        referenceId={props.paId}
        referenceType={CaptureSessionType.ProductApproval}
        text1={t('article.articleItemCode', {
          itemCode: props.itemCode,
        })}
        text2={props.paNumber}
        isOpen={isOpen}
        onClose={onClose}
        onPhotoReceived={(file) => {
          updateFile(file);
          onFileBrowserClose();
          onClose();
        }}
      />
      <input multiple={false} {...getInputProps()} />
    </Box>
  );
}

function HoverMenu({
  fileIdentifier,
  onFileBrowserOpen,
  onCaptureOpen,
  open,
  onRemoveFileOpen,
}: {
  fileIdentifier: string;
  onFileBrowserOpen: () => void;
  onCaptureOpen: () => void;
  open: () => void;
  onRemoveFileOpen: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Flex
      animation={fadeInRightAnimation()}
      pos="absolute"
      right={2}
      bottom={2}
      gap={1}
      display="none"
      bg="blackAlpha.800"
      padding={2}
      _light={{ bg: 'whiteAlpha.800' }}
      _groupHover={{ display: 'flex' }}
    >
      <Tooltip label={t('general.remove')}>
        <IconButton
          icon={<Icons.Close boxSize={6} />}
          aria-label={t('fileBrowser.removeFile')}
          size="sm"
          variant="texas-solid"
          onClick={() => {
            onRemoveFileOpen();
          }}
        />
      </Tooltip>
      <Tooltip label={t('fileBrowser.downloadFile')}>
        <IconButton
          icon={<Icons.Download boxSize={6} />}
          aria-label={t('fileBrowser.downloadFile')}
          size="sm"
          variant="no-bg"
          as="a"
          href={clientEndpoints.downloadFile(fileIdentifier)}
          target="_blank"
          download={true}
        />
      </Tooltip>
      <Tooltip label={t('fileBrowser.chooseFromLibrary')}>
        <IconButton
          icon={<Icons.OpenInNew boxSize={6} />}
          aria-label={t('fileBrowser.chooseFromLibrary')}
          size="sm"
          variant="no-bg"
          onClick={() => {
            onFileBrowserOpen();
          }}
        />
      </Tooltip>
      <Tooltip label={t('fileBrowser.uploadFile')}>
        <IconButton
          icon={<Icons.Upload boxSize={6} />}
          aria-label={t('fileBrowser.uploadFile')}
          size="sm"
          variant="no-bg"
          onClick={open}
        />
      </Tooltip>
      <Tooltip label={t('capturePhoto.capture')}>
        <IconButton
          icon={<Icons.Camera boxSize={6} />}
          aria-label={t('capturePhoto.capture')}
          size="sm"
          variant="no-bg"
          onClick={onCaptureOpen}
        />
      </Tooltip>
    </Flex>
  );
}
